import { useConversationArchiveUndo } from './use-conversation-archive-undo';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { V1ConversationsService } from 'src/api/generated';
import type {
  BulkConversationPassthroughUpdateParams,
  BulkOperationItemValidation,
  BulkOperationResponse,
  BulkSingleConversationUpdateParams,
  FunnelConversation,
  FunnelConversationDocument,
} from 'src/api/generated';
import { createOptimisticConversation } from 'src/modules/shared/communications/utils/cache/funnel-conversations/optimistic.util';
import {
  createConversationFromDocument,
  updateFunnelConversationCache,
} from 'src/modules/shared/communications/utils/cache/funnel-conversations/update.util';
import { affectedConversationQueries } from 'src/modules/shared/communications/utils/cache/funnel-conversations/query-impact.util';
import { getModelFromCache } from 'src/modules/shared/utils/cache/update.util';
import { captureConversationMutationCache } from 'src/modules/shared/communications/utils/cache/funnel-conversations/mutation-ownership.util';
import { rollbackConversationMutation } from 'src/modules/shared/communications/services/conversation-mutation-rollback.service';
import type { UpdateFunnelConversationContext } from 'src/modules/shared/communications/types/cache/funnel-conversations/optimistic.types';

interface BulkUpdateFunnelConversationParams {
  conversationIds: number[];
  data: Omit<BulkSingleConversationUpdateParams, 'id'>;
}

interface BulkUpdateFunnelConversationResponse {
  /** Requests a user bulk edit with a cancellable archive window. */
  requestBulkUpdateConversations: (
    params: BulkUpdateFunnelConversationParams
  ) => Promise<void>;
  loading: boolean;
  error: Error | null;
  response?: BulkOperationResponse;
  bulkUpdateConversations: (
    params: BulkUpdateFunnelConversationParams
  ) => Promise<BulkOperationResponse>;
}

export const useBulkUpdateFunnelConversation = (
  onSuccess?: (response: BulkOperationResponse) => void,
  onError?: (error: Error) => void
): BulkUpdateFunnelConversationResponse => {
  const confirmArchive = useConversationArchiveUndo();
  const queryClient = useQueryClient();

  const bulkUpdateFunnelConversations = async ({
    conversationIds,
    data,
  }: BulkUpdateFunnelConversationParams): Promise<BulkOperationResponse> => {
    const bulkParams: BulkConversationPassthroughUpdateParams = {
      conversations: conversationIds.map((id) => ({
        id,
        ...data,
      })),
    };

    return await V1ConversationsService.bulkUpdate(bulkParams);
  };

  const mutation = useMutation({
    mutationKey: ['crm/funnel-conversations/update'],
    mutationFn: bulkUpdateFunnelConversations,
    onMutate: async ({ conversationIds, data }) => {
      await queryClient.cancelQueries({
        queryKey: ['crm/documents/funnel-conversations'],
      });
      await queryClient.cancelQueries({
        queryKey: ['crm/funnel-conversations'],
      });

      const snapshots = new Map<number, UpdateFunnelConversationContext>();

      const indexToIdMap = new Map<number, number>();

      conversationIds.forEach((id, index) => {
        indexToIdMap.set(index, id);
        const previousConversation = getModelFromCache<FunnelConversation>(
          queryClient,
          ['crm/funnel-conversations'],
          id
        );
        const previousConversationDocument =
          getModelFromCache<FunnelConversationDocument>(
            queryClient,
            ['crm/documents/funnel-conversations'],
            id
          );

        const optimisticConversation = createOptimisticConversation(
          { id, data },
          previousConversation
        );
        // A document fallback supplies filter knowledge only, never a canonical revision.
        const beforeImpact =
          previousConversation ??
          (previousConversationDocument
            ? createConversationFromDocument(previousConversationDocument)
            : undefined);
        const searchScope = affectedConversationQueries(
          queryClient,
          createOptimisticConversation({ id, data }, beforeImpact),
          beforeImpact
        );

        updateFunnelConversationCache(
          queryClient,
          {
            params: { id, data },
            previousConversation,
            previousConversationDocument,
          },
          optimisticConversation
        );
        snapshots.set(id, {
          params: { id, data },
          previousConversation,
          previousConversationDocument,
          getCacheState: captureConversationMutationCache(queryClient, id),
          searchScope,
        });
      });

      return {
        snapshots,
        indexToIdMap,
      };
    },
    onError: (error, _variables, context) => {
      context?.snapshots.forEach((snapshot) =>
        rollbackConversationMutation(queryClient, snapshot)
      );
      onError?.(error);
    },
    onSuccess: (response, variables, context) => {
      if (response.report.batchErrors.length > 0) {
        context.snapshots.forEach((snapshot) =>
          rollbackConversationMutation(queryClient, snapshot)
        );
        onError?.(new Error(response.report.batchErrors[0].message));
        return;
      }

      const failedIds = new Set<number>();
      response.report.itemsIssues.forEach(
        (issue: BulkOperationItemValidation) => {
          if (!issue.errors.length) return;

          const conversationId = context.indexToIdMap.get(issue.index);
          if (!conversationId) return;

          failedIds.add(conversationId);
        }
      );

      if (failedIds.size > 0) {
        variables.conversationIds.forEach((id) => {
          if (!failedIds.has(id)) return;

          const snapshot = context.snapshots.get(id);
          if (snapshot) rollbackConversationMutation(queryClient, snapshot);
        });
      }

      onSuccess?.(response);
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    response: mutation.data,
    bulkUpdateConversations: mutation.mutateAsync,
    requestBulkUpdateConversations: async (params) => {
      if (!(await confirmArchive(params.conversationIds, params.data, true))) {
        return;
      }
      await mutation.mutateAsync(params);
    },
  };
};
